import React, { useState, useEffect } from 'react';
import { sinistroService } from '../../services/sinistroService';
import { useNotifier } from '../ui/Notifier';

const TIPO_OPTIONS = [
  { group: 'Veículos', options: ['Colisão', 'Roubo/Furto', 'Incêndio', 'Alagamento', 'Vidros', 'Guincho', 'Carro Reserva'] },
  { group: 'Vida/Saúde', options: ['Acidente Pessoal', 'Invalidez', 'Morte', 'Hospitalização'] },
  { group: 'Patrimonial', options: ['Incêndio Residencial', 'Roubo/Furto Residencial', 'Danos Elétricos', 'Responsabilidade Civil'] },
  { group: 'Outros', options: ['Terceiros', 'Danos Morais', 'Outros'] },
];

interface Props {
  sinistro?: any;
  documentoId?: string;
  isOpen: boolean;
  isEditing?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SinistroFormModal: React.FC<Props> = ({
  sinistro,
  documentoId,
  isOpen,
  isEditing = false,
  onClose,
  onSuccess
}) => {
  const [numeroSinistro, setNumeroSinistro] = useState('');
  const [tipo, setTipo] = useState('');
  const [dataOcorrencia, setDataOcorrencia] = useState('');
  const [descricao, setDescricao] = useState('');
  const [valorSinistro, setValorSinistro] = useState('');
  const [valorFranquia, setValorFranquia] = useState('');
  const [apolice, setApolice] = useState('');
  const [endosso, setEndosso] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [nomeSegurado, setNomeSegurado] = useState('');
  const [status, setStatus] = useState('aberto');
  const [documentoIdField, setDocumentoIdField] = useState(documentoId || '');
  const [submitting, setSubmitting] = useState(false);
  const notifier = useNotifier();

  useEffect(() => {
    if (sinistro && isEditing) {
      setNumeroSinistro(sinistro.numeroSinistro || '');
      setTipo(sinistro.tipo || '');
      setDataOcorrencia(sinistro.dataOcorrencia ? new Date(sinistro.dataOcorrencia).toISOString().split('T')[0] : '');
      setDescricao(sinistro.descricao || '');
      setValorSinistro(sinistro.valorSinistro ? String(sinistro.valorSinistro) : '');
      setValorFranquia(sinistro.valorFranquia ? String(sinistro.valorFranquia) : '');
      setApolice(sinistro.apolice || '');
      setEndosso(sinistro.endosso || '');
      setCpfCnpj(sinistro.cpfCnpj || '');
      setNomeSegurado(sinistro.nomeSegurado || '');
      setStatus(sinistro.status || 'aberto');
      setDocumentoIdField(sinistro.documentoId || documentoId || '');
    } else {
      setNumeroSinistro('');
      setTipo('');
      setDataOcorrencia('');
      setDescricao('');
      setValorSinistro('');
      setValorFranquia('');
      setApolice('');
      setEndosso('');
      setCpfCnpj('');
      setNomeSegurado('');
      setStatus('aberto');
      setDocumentoIdField(documentoId || '');
    }
  }, [sinistro, isEditing, isOpen, documentoId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: any = {
        numeroSinistro,
        tipo: tipo || undefined,
        dataOcorrencia: dataOcorrencia ? new Date(dataOcorrencia).toISOString() : undefined,
        descricao,
        valorSinistro: valorSinistro ? parseFloat(valorSinistro) : undefined,
        valorFranquia: valorFranquia ? parseFloat(valorFranquia) : undefined,
        apolice: apolice || undefined,
        endosso: endosso || undefined,
        cpfCnpj: cpfCnpj || undefined,
        nomeSegurado: nomeSegurado || undefined,
        status,
        documentoId: documentoIdField || undefined
      };

      if (isEditing && sinistro?.id) {
        await sinistroService.update(sinistro.id, payload);
      } else {
        await sinistroService.create(payload);
      }

      notifier.show({ message: isEditing ? 'Sinistro atualizado com sucesso!' : 'Sinistro criado com sucesso!', severity: 'success' });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao salvar sinistro', err);
      notifier.show({ message: 'Erro ao salvar sinistro', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full p-2 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-400';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: '#7A61B2' }}>
          <h3 className="text-sm font-semibold text-white">
            {isEditing ? 'Editar Sinistro' : 'Novo Sinistro'}
          </h3>
          <button type="button" onClick={onClose} className="text-white hover:text-purple-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4">
          <form onSubmit={handleSubmit} className="space-y-3" id="sinistro-form">
            {/* Identificação */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Número do Sinistro</label>
                <input className={inputClass} value={numeroSinistro} onChange={(e) => setNumeroSinistro(e.target.value)} placeholder="Ex: SIN-2024-001" />
              </div>
              <div>
                <label className={labelClass}>Tipo do Sinistro</label>
                <select className={inputClass} value={tipo} onChange={(e) => setTipo(e.target.value)}>
                  <option value="">Selecione...</option>
                  {TIPO_OPTIONS.map(g => (
                    <optgroup key={g.group} label={g.group}>
                      {g.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            {/* Referências */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Apólice</label>
                <input className={inputClass} value={apolice} onChange={(e) => setApolice(e.target.value)} placeholder="Número da apólice" />
              </div>
              <div>
                <label className={labelClass}>Endosso</label>
                <input className={inputClass} value={endosso} onChange={(e) => setEndosso(e.target.value)} placeholder="Número do endosso" />
              </div>
            </div>

            {/* Segurado */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>CPF/CNPJ do Segurado</label>
                <input className={inputClass} value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)} placeholder="000.000.000-00" />
              </div>
              <div>
                <label className={labelClass}>Nome do Segurado</label>
                <input className={inputClass} value={nomeSegurado} onChange={(e) => setNomeSegurado(e.target.value)} placeholder="Nome completo" />
              </div>
            </div>

            {/* Ocorrência */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Data de Ocorrência</label>
                <input type="date" className={inputClass} value={dataOcorrencia} onChange={(e) => setDataOcorrencia(e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="aberto">Aberto</option>
                  <option value="em_andamento">Em Andamento</option>
                  <option value="fechado">Fechado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>

            {/* Valores */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Valor do Sinistro (R$)</label>
                <input type="number" step="0.01" className={inputClass} value={valorSinistro} onChange={(e) => setValorSinistro(e.target.value)} placeholder="0,00" />
              </div>
              <div>
                <label className={labelClass}>Valor da Franquia (R$)</label>
                <input type="number" step="0.01" className={inputClass} value={valorFranquia} onChange={(e) => setValorFranquia(e.target.value)} placeholder="0,00" />
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className={labelClass}>Descrição</label>
              <textarea className={inputClass} value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={3} placeholder="Descreva o sinistro..." />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-gray-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs rounded border border-gray-300 hover:bg-gray-100">
            Cancelar
          </button>
          <button form="sinistro-form" type="submit" disabled={submitting} className="px-4 py-2 text-xs rounded text-white disabled:opacity-50" style={{ backgroundColor: '#7A61B2' }}>
            {submitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar Sinistro')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SinistroFormModal;
